import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Mail, Send, Loader2, ShieldAlert, KeyRound, Trash2, AlertTriangle } from "lucide-react";

function ChangePasswordCard() {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [nw2, setNw2] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (nw.length < 8) { toast.error("Yeni şifre en az 8 karakter olmalıdır"); return; }
    if (nw !== nw2) { toast.error("Yeni şifreler eşleşmiyor"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Şifreniz güncellendi");
      setCur(""); setNw(""); setNw2("");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Card className="p-6">
      <h3 className="flex items-center gap-2 font-heading font-bold text-[#0a192f]"><KeyRound className="h-4 w-4 text-emerald-600" />Yönetici Şifresini Değiştir</h3>
      <p className="mt-1 text-sm text-slate-500">Güvenliğiniz için şifrenizi düzenli olarak güncelleyin</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <div><Label>Mevcut Şifre</Label><Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="current-password-input" className="mt-1.5" /></div>
        <div><Label>Yeni Şifre</Label><Input type="password" value={nw} onChange={(e) => setNw(e.target.value)} data-testid="new-password-input" className="mt-1.5" /></div>
        <div><Label>Yeni Şifre (Tekrar)</Label><Input type="password" value={nw2} onChange={(e) => setNw2(e.target.value)} data-testid="new-password-confirm-input" className="mt-1.5" /></div>
      </div>
      <Button onClick={submit} disabled={saving} className="mt-5 bg-emerald-600 hover:bg-emerald-700" data-testid="change-password-button">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Şifreyi Güncelle"}
      </Button>
    </Card>
  );
}

function CleanupCard() {
  const [busy, setBusy] = useState(false);
  const cleanup = async () => {
    if (!window.confirm("Örnek/test firmaları ve tüm test belgeleri kalıcı olarak silinecek. Devam edilsin mi?")) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/cleanup-sample-data");
      toast.success(r.data.count > 0 ? `${r.data.count} test firması ve belgeleri temizlendi` : "Temizlenecek test verisi bulunamadı");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Card className="border-red-200 p-6">
      <h3 className="flex items-center gap-2 font-heading font-bold text-red-700"><AlertTriangle className="h-4 w-4" />Gerçek Ortama Geçiş: Test Verilerini Temizle</h3>
      <p className="mt-1 text-sm text-slate-500">
        Deneme sırasında oluşturulan örnek firmaları (adı "TEST_" ile başlayanlar ve "Test AŞ") ve bunlara ait tüm belge, sürüm, müşteri hesabı ve Word dosyalarını kalıcı olarak siler.
        Gerçek firmalarınız etkilenmez.
      </p>
      <Button onClick={cleanup} disabled={busy} variant="outline" className="mt-4 border-red-300 text-red-700 hover:bg-red-50" data-testid="cleanup-sample-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="mr-2 h-4 w-4" />Test Verilerini Temizle</>}
      </Button>
    </Card>
  );
}

export default function Settings() {
  const [cfg, setCfg] = useState(null);
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  useEffect(() => { api.get("/admin/smtp").then((r) => setCfg(r.data)); }, []);

  const save = async () => {
    setSaving(true);
    const body = { ...cfg };
    if (pw) body.password = pw; else body.password = null;
    try { await api.put("/admin/smtp", body); toast.success("Ayarlar kaydedildi"); setPw(""); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testTo) { toast.error("Test e-posta adresi girin"); return; }
    setTesting(true);
    try {
      const r = await api.post("/admin/smtp/test", { to: testTo });
      toast.success(r.data.status === "captured" ? "Test yakalandı (dev modu, gerçek gönderim yok)" : "Test e-postası gönderildi");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setTesting(false); }
  };

  if (!cfg) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-emerald-600" /></div>;
  const set = (k, v) => setCfg({ ...cfg, [k]: v });

  return (
    <div className="fade-in mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-[#0a192f]">Ayarlar</h1>
        <p className="mt-1 text-slate-500">E-posta yapılandırması, şifre yönetimi ve veri temizliği</p>
      </div>

      <ChangePasswordCard />

      <Card className="p-6">
        <div className="mb-5 flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3">
          <div>
            <div className="flex items-center gap-2 font-semibold text-[#0a192f]"><ShieldAlert className="h-4 w-4 text-amber-600" />Geliştirme Modu (Test Yakalama)</div>
            <p className="text-xs text-slate-500">Açıkken e-postalar gerçekten gönderilmez, sadece kaydedilir/loglanır.</p>
          </div>
          <Switch checked={cfg.dev_mode} onCheckedChange={(v) => set("dev_mode", v)} data-testid="dev-mode-switch" />
        </div>

        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label>SMTP Sunucu (Host)</Label><Input value={cfg.host} onChange={(e) => set("host", e.target.value)} placeholder="smtp.ornek.com" data-testid="smtp-host-input" className="mt-1.5" /></div>
            <div><Label>Port</Label><Input type="number" value={cfg.port} onChange={(e) => set("port", parseInt(e.target.value) || 587)} data-testid="smtp-port-input" className="mt-1.5" /></div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label>Kullanıcı Adı</Label><Input value={cfg.username} onChange={(e) => set("username", e.target.value)} data-testid="smtp-username-input" className="mt-1.5" /></div>
            <div><Label>Şifre {cfg.configured && "(değiştirmek için girin)"}</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder={cfg.configured ? "••••••••" : ""} data-testid="smtp-password-input" className="mt-1.5" /></div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label>Gönderen Adresi</Label><Input value={cfg.from_address} onChange={(e) => set("from_address", e.target.value)} placeholder="belge@firma.com" data-testid="smtp-from-input" className="mt-1.5" /></div>
            <div><Label>Gönderen Adı</Label><Input value={cfg.from_name} onChange={(e) => set("from_name", e.target.value)} data-testid="smtp-fromname-input" className="mt-1.5" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={cfg.use_ssl} onCheckedChange={(v) => set("use_ssl", v)} data-testid="smtp-ssl-switch" /> SSL kullan (genellikle 465 portu)
          </label>
        </div>

        <Button onClick={save} disabled={saving} className="mt-6 bg-emerald-600 hover:bg-emerald-700" data-testid="smtp-save-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="mr-2 h-4 w-4" />Ayarları Kaydet</>}
        </Button>
      </Card>

      <Card className="p-6">
        <h3 className="font-heading font-bold text-[#0a192f]">Test E-postası Gönder</h3>
        <p className="mt-1 text-sm text-slate-500">Ayarlarınızın çalıştığını doğrulayın</p>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="alici@ornek.com" data-testid="test-email-input" />
          <Button onClick={sendTest} disabled={testing} variant="outline" data-testid="send-test-button">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-2 h-4 w-4" />Gönder</>}
          </Button>
        </div>
      </Card>

      <CleanupCard />
    </div>
  );
}
